/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpPolicySetModule
 */

import {
    HTTPSecurityActionType,
    IHTTPLocalFile,
    IHTTPSecurityAction,
    IHTTPSecurityActionRateProfile,
    IRateLimiter,
} from 'generated-types';

import { HTTPSecurityAction } from 'object-types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';

import {
    HTTPRedirectActionConfigItem,
    RateLimiterActionConfigItem,
} from 'ng/modules/message-items/factories';

type THTTPSecurityActionRateProfile = Omit<IHTTPSecurityActionRateProfile,
'action' | 'rate_limiter'>;

export interface IHTTPSecurityActionRateProfileConfig extends THTTPSecurityActionRateProfile {
    action: RateLimiterActionConfigItem;
    rate_limiter?: MessageItem<IRateLimiter>;
}

type THTTPSecurityAction = Omit<IHTTPSecurityAction, 'rate_profile' | 'file'>;

interface IHTTPSecurityPolicyRuleActionConfig extends THTTPSecurityAction {
    rate_profile?: MessageItem<IHTTPSecurityActionRateProfileConfig>;
    file?: MessageItem<IHTTPLocalFile>;
}

/**
 * @description HTTPSecurityRuleAction ConfigItem.
 *
 * @author Nisar Nadaf, Umesh Chovatiya
 */
export class HTTPSecurityActionConfigItem extends
    MessageItem<IHTTPSecurityPolicyRuleActionConfig> {
    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPSecurityAction,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Getter for rateLimiter redirect action.
     */
    public get rateLimiterAction(): HTTPRedirectActionConfigItem {
        const { rate_profile: rateProfile } = this.config;

        return rateProfile.config.action.config.redirect;
    }

    /** @override */
    public modifyConfigDataBeforeSave(): void {
        const { action } = this.config;

        super.modifyConfigDataBeforeSave();

        if (action !== HTTPSecurityActionType.HTTP_SECURITY_ACTION_RATE_LIMIT) {
            delete this.config.rate_profile;
        }
    }

    /**
     * Create File config.
     */
    public createFile(): void {
        this.safeSetNewChildByField('file');
    }

    /** @override */
    protected requiredFields(): string[] {
        return [
            'rate_profile',
        ];
    }
}
